import React, { useRef, useState } from 'react';
import {
  Box, Tab, Tabs, TextField, Typography, LinearProgress, Alert, Link,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useInlineImageUpload } from './useInlineImageUpload';
import { normaliseInlineImageSrc } from './inlineImageSrc';

interface Props {
  value: string;
  onChange: (next: string) => void;
  label?: string;
  rows?: number;
  required?: boolean;
  disabled?: boolean;
}

/**
 * Description editor with GitHub-style Edit/Preview tabs.
 *
 * Edit shows the raw markdown — including the full image URL — so the text stays copyable and
 * editable. Preview renders it, so pasted screenshots appear inline. Pasting or dropping an
 * image uploads it and splices the markdown in at the caret (see useInlineImageUpload).
 *
 * The stored value is plain markdown, which is what makes this work end-to-end: the same
 * string is written verbatim into the linked GitHub issue body, where GitHub's own renderer
 * displays the images inline exactly as this preview does.
 */
const MarkdownDescriptionField: React.FC<Props> = ({
  value, onChange, label = 'Description', rows = 6, required = false, disabled = false,
}) => {
  const [tab, setTab] = useState<'edit' | 'preview'>('edit');
  const [isDragging, setIsDragging] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { isUploading, error, clearError, handlePaste, handleDrop } = useInlineImageUpload({
    value, onChange, textareaRef,
  });

  const onDrop = (e: React.DragEvent<HTMLElement>) => {
    setIsDragging(false);
    handleDrop(e);
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          border: '1px solid',
          borderColor: isDragging ? 'primary.main' : 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          transition: 'border-color 120ms ease',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#FAFBFC' }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{ minHeight: 38, '& .MuiTab-root': { minHeight: 38, textTransform: 'none', fontSize: 13, py: 0 } }}
          >
            <Tab value="edit" label="Edit" />
            <Tab value="preview" label="Preview" />
          </Tabs>
          <Typography variant="caption" sx={{ color: 'text.secondary', pr: 1.5, display: { xs: 'none', sm: 'block' } }}>
            Paste or drop an image to attach it
          </Typography>
        </Box>

        {isUploading && <LinearProgress sx={{ height: 2 }} />}

        {tab === 'edit' ? (
          <TextField
            inputRef={textareaRef}
            fullWidth
            multiline
            rows={rows}
            value={value}
            onChange={e => onChange(e.target.value)}
            onPaste={handlePaste}
            onDrop={onDrop}
            onDragOver={(e: React.DragEvent) => {
              if (e.dataTransfer?.types?.includes('Files')) {
                e.preventDefault();
                setIsDragging(true);
              }
            }}
            onDragLeave={() => setIsDragging(false)}
            required={required}
            disabled={disabled}
            placeholder={`Describe the ${label.toLowerCase()}…  Markdown supported.`}
            variant="standard"
            InputProps={{ disableUnderline: true, sx: { p: 1.5, fontSize: 14, alignItems: 'flex-start' } }}
          />
        ) : (
          <Box
            sx={{
              p: 1.5,
              minHeight: rows * 23,
              fontSize: 14,
              overflowWrap: 'anywhere',
              '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1, border: '1px solid', borderColor: 'divider', display: 'block', my: 1 },
              '& p': { my: 0.5 },
              '& p:first-of-type': { mt: 0 },
              '& pre': { bgcolor: '#F5F7F9', p: 1, borderRadius: 1, overflowX: 'auto' },
              '& code': { bgcolor: '#F5F7F9', px: 0.5, borderRadius: 0.5, fontSize: 13 },
              '& table': { borderCollapse: 'collapse', '& td, & th': { border: '1px solid #ddd', px: 1, py: 0.5 } },
            }}
          >
            {value.trim() ? (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  img: ({ src, alt, ...rest }) => (
                    <img {...rest} src={normaliseInlineImageSrc(src)} alt={alt || ''} />
                  ),
                }}
              >
                {value}
              </ReactMarkdown>
            ) : (
              <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                Nothing to preview.
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 1 }} onClose={clearError}>{error}</Alert>
      )}

      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
        Supports{' '}
        <Link href="https://docs.github.com/get-started/writing-on-github" target="_blank" rel="noopener noreferrer" underline="hover">
          Markdown
        </Link>
        . Pasted images upload automatically and appear in Preview.
      </Typography>
    </Box>
  );
};

export default MarkdownDescriptionField;
